import React from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';

import type { MetricsRange } from '../../services/channelDashboardApi';
import { relativeSince } from './formatMetrics';

const RANGES: MetricsRange[] = ['7d', '30d', '90d'];
const RANGE_LABEL: Record<MetricsRange, string> = { '7d': '7 dias', '30d': '30 dias', '90d': '90 dias' };

interface Props {
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncedAt?: string;
  range: MetricsRange;
  onRange: (r: MetricsRange) => void;
  onSync: () => void;
  syncing: boolean;
  onExport: () => void;
  canExport: boolean;
}

export const DashboardTopBar: React.FC<Props> = ({ syncStatus, lastSyncedAt, range, onRange, onSync, syncing, onExport, canExport }) => {
  const busy = syncing || syncStatus === 'syncing';
  return (
    <div className="cdash-topbar">
      <span className="cdash-sync" data-testid="cdash-sync-status">
        <span className="cdash-syncdot" data-status={syncStatus} />
        {syncStatus === 'syncing'
          ? 'Sincronizando…'
          : syncStatus === 'error'
            ? 'Falha no último sync'
            : `Sincronizado ${relativeSince(lastSyncedAt)}`}
      </span>
      <span className="cdash-spacer" />
      <span className="cdash-chips" data-testid="cdash-range">
        {RANGES.map((r) => (
          <button
            key={r}
            className="cdash-chip"
            aria-pressed={range === r}
            onClick={() => onRange(r)}
            data-testid={`cdash-range-${r}`}
          >
            {RANGE_LABEL[r]}
          </button>
        ))}
      </span>
      {/* Exportar — padrão secundário shared (F08): Button 38px, ícone download 16, hover */}
      <Button
        variant="secondary"
        size="md"
        icon="download"
        onClick={onExport}
        disabled={!canExport}
        data-testid="cdash-export"
      >
        Exportar
      </Button>
      {/* Refresh — padrão único (F07): casca 36×36 bordada, ícone 16, hover ghost, ícone-only */}
      <button
        type="button"
        onClick={onSync}
        disabled={busy}
        title="Atualizar"
        aria-label="Atualizar"
        className="h-9 w-9 rounded-lg inline-flex items-center justify-center transition-colors flex-none disabled:opacity-50"
        style={{
          border: '1px solid var(--theme-input-border)',
          backgroundColor: 'var(--theme-card-background)',
          color: 'var(--theme-muted-text)',
        }}
        onMouseEnter={(e) => {
          if (!busy) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
        }}
        data-testid="cdash-refresh"
      >
        <Icon name="refresh" className={`w-4 h-4${busy ? ' animate-spin' : ''}`} />
      </button>
    </div>
  );
};
